<template>
  <div>
    <article class="message is-danger" v-if="!token">
      <div class="message-body">
        You have not signed in yet. Please go back to <a href="/">home page</a> to sign in first.
      </div>
    </article>
    <div v-if="token">
      <div>
        <div>
          <div v-if="waiting">
            <span class="icon is-medium is-size-4">
              <i class="fas fa-spinner fa-pulse"></i>
            </span>
          </div>
          <div v-else>
            <div class="map-container mb-6">
              <GmapMap
                :center="mapCenter"
                :zoom="3"
                map-type-id="terrain"
                style="width: 100%; height: 800px">
                <GmapMarker v-for="(v, i) in showingVessels"
                  :key="'gmm-'+i"
                  :position="v.position"
                  :icon="v.icon"
                  :label="v.label"
                  @click="v.opened = !v.opened"
                />
                <GmapInfoWindow v-for="(v, i) in showingVessels"
                  :key="'gmi-'+i"
                  :options="v.infoWindow"
                  :position="v.position"
                  :opened="v.opened"
                  @closeclick="v.opened=false">
                </GmapInfoWindow>
              </GmapMap>
            </div>

            <div class="container px-2 table-container">
              <table class="table is-fullwidth my-table">
                <thead>
                  <th>
                    <span class="is-clickable" @click="changeSortOption('id')">Id</span>
                    <span class="icon" v-if="sortOption.field == 'id'">
                      <i class="fas" :class="{'fa-sort-up': sortOption.asc, 'fa-sort-down': !sortOption.asc}"></i>
                    </span>
                  </th>
                  <th>
                    <span class="is-clickable" @click="changeSortOption('shipmentNumber')">Shipment#</span>
                    <span class="icon" v-if="sortOption.field == 'shipmentNumber'">
                      <i class="fas" :class="{'fa-sort-up': sortOption.asc, 'fa-sort-down': !sortOption.asc}"></i>
                    </span>
                  </th>
                  <th>
                    <span class="is-clickable" @click="changeSortOption('bookingId')">External doc id</span>
                    <span class="icon" v-if="sortOption.field == 'bookingId'">
                      <i class="fas" :class="{'fa-sort-up': sortOption.asc, 'fa-sort-down': !sortOption.asc}"></i>
                    </span>
                  </th>
                  <th>
                    <span class="is-clickable" @click="changeSortOption('locationName')">Location</span>
                    <span class="icon" v-if="sortOption.field == 'locationName'">
                      <i class="fas" :class="{'fa-sort-up': sortOption.asc, 'fa-sort-down': !sortOption.asc}"></i>
                    </span><br/>
                    <div class="field header-select">
                      <div class="control">
                        <div class="select">
                          <select v-model="filters.location.selected">
                            <option v-for="(lo, i) in filters.location.options" :value="lo" :key="'lo-'+i">{{lo}}</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </th>
                  <th>
                    <span class="is-clickable" @click="changeSortOption('vessel')">Vessel</span>
                    <span class="icon" v-if="sortOption.field == 'vessel'">
                      <i class="fas" :class="{'fa-sort-up': sortOption.asc, 'fa-sort-down': !sortOption.asc}"></i>
                    </span>
                  </th>
                  <th>
                    <span class="is-clickable" @click="changeSortOption('containers')">Containers</span>
                    <span class="icon" v-if="sortOption.field == 'containers'">
                      <i class="fas" :class="{'fa-sort-up': sortOption.asc, 'fa-sort-down': !sortOption.asc}"></i>
                    </span>
                  </th>
                  <th>
                    <span class="is-clickable" @click="changeSortOption('expectedShippingDate')">Expected shipping</span>
                    <span class="icon" v-if="sortOption.field == 'expectedShippingDate'">
                      <i class="fas" :class="{'fa-sort-up': sortOption.asc, 'fa-sort-down': !sortOption.asc}"></i>
                    </span>
                  </th>
                  <th>
                    <span class="is-clickable" @click="changeSortOption('expectedDeliveryDate')">Expected delivery</span>
                    <span class="icon" v-if="sortOption.field == 'expectedDeliveryDate'">
                      <i class="fas" :class="{'fa-sort-up': sortOption.asc, 'fa-sort-down': !sortOption.asc}"></i>
                    </span>
                  </th>
                </thead>
                <tbody>
                  <template v-for="(ib, i) in showingIbs">
                    <tr>
                      <td class="id-cell" @click="toggleOpen(ib)">
                        <i class="fas" :class="{'fa-angle-right': !ib.opened, 'fa-angle-down': ib.opened,}"></i>&nbsp;
                        <span>{{ib.id}}</span>
                      </td>
                      <td>{{ib.shipmentNumber}}</td>
                      <td>
                        <router-link :to="'/inbound/' + ib.id">{{ib.bookingId}}</router-link>
                      </td>
                      <td>{{ib.locationName}}</td>
                      <td>
                        <span v-if="vesselMap[ib.vessel.trim()]" class="tag is-black"
                          :style="{'background-color': vesselMap[ib.vessel.trim()].color, 'cursor': 'pointer'}"
                          @click="activeVesselIndex = vesselMap[ib.vessel.trim()].index; Object.values(vesselMap).forEach(v => {v.opened = false}); vesselMap[ib.vessel.trim()].opened = true">
                          {{ ib.vessel }}
                        </span>
                        <span v-else>
                          <span v-if="ib.vessel" class="tag">{{ ib.vessel }}</span>
                        </span>
                      </td>
                      <td>
                        <div v-if="ib.containers" style="width:150px">
                          <div v-for="(c,i) in ib.containers.split(/,|\//)" :key="'ibc-'+i">
                            {{c}}
                          </div>
                        </div>
                      </td>
                      <td>{{ib.expectedShippingDate}}</td>
                      <td>{{ib.expectedDeliveryDate}}</td>
                    </tr>
                    <tr v-if="ib.opened">
                      <td></td>
                      <td :colspan="8" class="pb-6">
                        <span v-if="!ib.items" class="icon is-medium is-size-6">
                          <i class="fas fa-spinner fa-pulse"></i>
                        </span>
                        <table v-if="ib.items && ib.totals" class="table is-narrow is-hoverable sub-items-table">
                          <thead>
                            <th>PO</th>
                            <th>Item</th>
                            <th>Name</th>
                            <th>Description</th>
                            <th class="num-cell">Quantity expected</th>
                            <th>Containers</th>
                            <th class="num-cell">Unit price</th>
                            <th class="num-cell">Total price</th>
                            <th class="num-cell">Tarriff %</th>
                            <th class="num-cell">Total Tarriff</th>
                          </thead>
                          <tbody>
                            <tr v-for="(ibi, j) in ib.items" :key="'ib-' + ib.id + '-j-' + j">
                              <td>
                                <span v-if="!ibi.po">{{ ibi.purchaseOrderName }}</span>
                                <router-link v-else :to="'/purchase-order/' + ibi.po.id">{{ibi.purchaseOrderName }}</router-link>
                              </td>
                              <td>{{ ibi.itemId }}</td>
                              <td>{{ ibi.shipmentItemName }}</td>
                              <td>{{ ibi.shipmentItemDescription }}</td>
                              <td class="num-cell">{{ ibi.quantityExpected }}</td>
                              <td>{{ ibi.containerInfo.replaceAll(',', ', ') }}</td>
                              <td class="num-cell">{{ ibi.poItem && formatPrice(ibi.poItem.rate) }}</td>
                              <td class="num-cell">{{ ibi.poItem && formatPrice(ibi.poItem.rate * ibi.quantityExpected) }}</td>
                              <td class="num-cell">{{ (ibi.poItem && ibi.tarriff != null) ? ('' + ibi.tarriff + '%') : 'N/A' }}</td>
                              <td class="num-cell">{{ (ibi.poItem && ibi.tarriff != null) ? formatPrice(ibi.poItem.rate * ibi.quantityExpected * ibi.tarriff / 100) : 'N/A' }}</td>
                            </tr>
                          </tbody>
                          <tfoot>
                            <tr>
                              <th>Total</th>
                              <th></th>
                              <th></th>
                              <th></th>
                              <th class="num-cell">{{ (ib.totals && ib.totals.quantity) || '' }}</th>
                              <th></th>
                              <th></th>
                              <th class="num-cell">{{ (ib.totals && ib.totals.amount) ? formatPrice(ib.totals.amount) : '' }}</th>
                              <th></th>
                              <th class="num-cell">{{ (ib.totals && ib.totals.tarriff) ? formatPrice(ib.totals.tarriff) : '' }}</th>
                            </tr>
                          </tfoot>
                        </table>
                      </td>
                    </tr>
                  </template>
                  
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      
      <div v-if="error" class="notification is-danger is-light">
        <button class="delete" @click="error=''"></button>
        {{error}}
      </div>

    </div>
  </div>
</template>

<script>
import dateFormat from 'dateformat'
import { Converter } from 'showdown'

const converter = new Converter()
const dateFormatter = (date) => {
  const d = new Date(date * 1000)
  return dateFormat(d, 'yyyy-mm-dd HH:MM')
}

var colors = ['#9e0142', '#d53e4f', '#f46d43', '#fdae61', '#fee08b', '#e6f598', '#abdda4', '#66c2a5', '#3288bd', '#5e4fa2']
var primaryColor = 'hsl(348, 100%, 61%)'

function getColor(i) {
  if (i < colors.length) {
    return colors[i]
  }
  var n = Math.floor(Math.random() * 8000000)
  var b = n % 200
  n = Math.floor(n / 200)
  var g = n % 200
  var r = Math.floor(n / 200)
  return 'rgb(' + r + ', ' + g + ', ' + b + ')'
}

const svgMarker = {
  path: "M11.437 17.608 3.354 22.828l8.336 -21.536 8.337 21.536L11.944 17.608l-0.253 -0.163 -0.254 0.163Z",
  fillColor: "hsl(348, 100%, 61%)",
  fillOpacity: 1,
  strokeWeight: 1,
  strokeColor: 'white',
  rotation: 0,
  scale: 1.5,
  anchor: {x: 12, y: 12},
};

export default {
  name: 'InboundsMap',
  components: {
  },
  data () {
    return {
      error: '',
      waiting: false,
      search: '',
      timeFilter: 'All',
      vessels: [],
      activeVesselIndex: -1,
      vesselMap: {},
      inbounds: [],
      sortOption: { field: 'vessel', asc: true },
      search: '',
      filters: {
        vendor: {options: [], selected: 'All'},
        status: {options: [], selected: 'All'},
        location: {options: [], selected: 'All'},
      },
      uploadLandedCostsModal: {
        opened: false,
      },
    }
  },
  computed: {
    server () {
      return this.$store.state.config.server
    },
    token () {
      return this.$store.state.user.token
    },
    isVendor () {
      return this.$store.state.user.groups.includes('Vendors')
    },
    isVendorAdmin () {
      return this.$store.state.user.groups.includes('VendorAdmin')
    },
    inboundId () {
      return this.$route.params.inboundId
    },
    showingIbs () {
      var vm = this
      
      var filteredIbs = vm.inbounds.filter(p => 
        (this.filters.location.selected == 'All' || this.filters.location.selected == p.locationName)
      )

      var mappedIbs = filteredIbs.map(ib => {
        if (!ib.shipmentCreatedDate) {
          ib.shipmentCreatedDate = dateFormat(new Date(ib.createdAt * 1000) , 'isoDate')
        }
        if (!ib.lastModifiedDate) {
          ib.lastModifiedDate = dateFormat(new Date(ib.updatedAt * 1000) , 'isoDate')
        }
        return ib
      })
      
      var sort = vm.sortOption
      var sortedIbs = mappedIbs.sort((a, b) => {
        var va = a[sort.field]
        var vb = b[sort.field]
        if (va == null || vb == null) {
          if (va) {
            return sort.asc ? 1 : -1
          }
          if (vb) {
            return sort.asc ? -1 : 1
          }
          return 0
        }
        if (sort.field == 'id' ) {
          return sort.asc ? va - vb : vb - va
        }
        if (sort.field == 'vessel') {
          va = va.trim()
          vb = vb.trim()
          if (this.vesselMap[va] && !this.vesselMap[vb]) {
            return sort.asc ? -1 : 1
          }
          if (!this.vesselMap[va] && this.vesselMap[vb]) {
            return sort.asc ? 1 : -1
          }
        }
        return sort.asc ? va.localeCompare(vb) : vb.localeCompare(va)
      })
      return sortedIbs
    },
    showingVessels () {
      var filteredVessels = this.vessels.filter(v => {
        if (!v.inbounds.length) {
          return false
        }
        if (this.filters.location.selected == 'All') {
          return true
        }
        for (var ib of v.inbounds) {
          if (ib.locationName == this.filters.location.selected) {
            return true
          }
        }
        return false
      })
      return filteredVessels
    },
    mapCenter () {
      if (this.vessels.length == 0) {
        return {lat: 0, lng: 0}
      }
      if (this.activeVesselIndex < 0) {
        return this.vessels[0].position
      }
      var activeVessel = this.vessels[this.activeVesselIndex]
      return activeVessel.position
    },
  },
  methods: {
    getVesselsWithInbounds () {
      this.waiting = true
      this.$http.get(this.server + '/myapp/get-vessels-with-inbounds/').then(resp => {
        var inbounds = resp.body.inbounds.map(i => {
          i.opened = false
          i.items = null
          i.totals = null
          return i
        })
        this.inbounds = inbounds
        this.makeVessels(resp.body.vessels)
        this.collectFiltersOptions()
        this.waiting = false
      }, err => {
        this.error = err.body
        this.waiting = false
      })
    },
    collectFiltersOptions () {
      var filters = {
        location: {options: this.collectOptions('locationName'), selected: 'All'},
      }
      this.filters = filters
    },
    collectOptions(fieldName) {
      var values = new Set()
      for (var c of this.inbounds) {
        values.add(c[fieldName].trim())
      }
      var vs = Array.from(values)
      vs.sort()
      vs.unshift('All')
      return vs
    },
    toggleOpen (p) {
      p.opened = !p.opened
      if (!p.items) {
        this.$http.get(this.server + '/myapp/get-inbound-items-with-pos/' + p.id).then(resp => {
          p.items = resp.body.map(pi => {
            if (pi.po.locationId == 9) {
              pi.tarriff = pi.poItem.dutyPercentageCanada
            } else {
              pi.tarriff = pi.poItem.dutyPercentage
            }
            return pi
          })
          p.totals = this.computeTotals(p.items)
        })
      }
    },
    computeTotals (items) {
      var totals = {
        quantity: 0,
        amount: 0,
        tarriff: 0,
      }
      for (var item of items) {
        totals.quantity += item.quantityExpected
        totals.amount += (item.poItem && item.poItem.rate) ? (item.poItem.rate * item.quantityExpected) : 0
        totals.tarriff += (item.poItem && item.poItem.tarriff) ? (item.poItem.rate * item.quantityExpected * item.tarriff / 100) : 0
      }
      return totals
    },
    changeSortOption (field) {
      this.sortOption = {
        field: field,
        asc: field == this.sortOption.field ? !this.sortOption.asc : true
      }
    },
    formatPrice (value) {
      if (!value) {
        return ''
      }
      return '$' + value.toLocaleString('en-US', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
        useGrouping: false
      })
    },
    makeVessels (vessels) {
      var vesselInbounds = {}
      for (var i of this.inbounds) {
        if (i.vessel) {
          var vessel = i.vessel.trim()
          if (!vesselInbounds[vessel]) {
            vesselInbounds[vessel] = []
          }
          vesselInbounds[vessel].push(i)
        }
      }
      var vesselMap = {}
      for (var i=0;i<vessels.length;i++) {
        var v = vessels[i]
        v.index = i
        v.inbounds = vesselInbounds[v.name] || []
        v.color = getColor(i)
        v.opened = false
        for (var vi of v.inbounds) {
          if (vi.id == this.inboundId) {
            v.color = primaryColor
            v.opened = true
            this.activeVesselIndex = i
          }
        }
        this.makeUpVessel(v)
        vesselMap[v.name.trim()] = v
      }
      this.vessels = vessels
      this.vesselMap = vesselMap
    },
    makeUpVessel (v) {
      v.icon = {...svgMarker}
      v.icon.fillColor = v.color
      var position = v.data.position
      if (position) {
        v.position = { lat: position.lat, lng: position.lon }
        v.icon.rotation = position.heading
      }
      v.label = {text: (v.inbounds.length ? v.inbounds.map(i => i.id).join(', ') : v.name), fontWeight: 'bold'}
      v.infoWindow = {
        content: this.makeVesselContent(v),
        pixelOffset: {width: 0, height: -25},
      }
    },
    makeVesselContent (vessel) {
      var title = ''
      if (vessel.inbounds.length > 0) {
        var inboundIds = vessel.inbounds.map(i => i.id).join(', ')
        var locationName = vessel.inbounds[0].locationName
        title = `<h5 class="title is-5">${inboundIds} -> ${locationName}</h5><h5 class="subtitle is-5">${vessel.name}</h5>`
      }
      var {position, voyage, summary} = vessel.data
      var content = converter.makeHtml(summary.position)
      content = `<div class="mb-5 is-size-6">${title}${content}</div>`
      var rows = [
        {
          data: position,
          fields: [
            {name: 'areaName', label: 'Area'},
            {name: 'lat', label: 'Latitude'},
            {name: 'lon', label: 'Longitude'},
            {name: 'heading', label: 'Heading'},
          ],
        },
        {
          data: position,
          fields: [
            {name: 'navigationalStatus', label: 'Status'},
            {name: 'speed', label: 'Speed'},
            {name: 'course', label: 'Course'},
            {name: 'timestamp', label: 'Timestamp', formatter: dateFormatter},
          ],
        },
        {
          data: voyage,
          fields: [
            {name: 'reportedDestination', label: 'Destination'},
            {name: 'eta', label: 'ETA', formatter: dateFormatter},
            {name: 'progress', label: 'Progress'},
            {name: 'arrivalLabel', label: 'Arrival Label'},
          ],
        },
      ]
      for (var row of rows) {
        content += this.makeLevelRow(row.data, row.fields)
      }
      content = `<div class="ml-3 mr-3 mb-3">${content}</div>`
      return content
    },
    makeLevelRow (data, fields) {
      var blocks = fields.map(field => this.makeLevelBlock(data, field))
      return `
        <div class="columns">
          ${blocks.join('')}
        </div>
      `
    },
    makeLevelBlock (data, field) {
      var label = field.label
      var value = data[field.name]
      if (value) {
        if (field.formatter) {
          value = field.formatter(value)
        } else {
          value = value.toString()
        }
      } else {
        value = ''
      }
      return `
        <div class="column" style="padding-top:6px;padding-bottom:6px">
          <p class="heading">${label}</p>
          <p class="title is-6">${value}</p>
        </div>
      `
    },
  },
  mounted () {
    this.getVesselsWithInbounds()
  },
}
</script>

<style scoped>
.id-cell {
  min-width: 80px;
  cursor: pointer;
}
.sub-items-table {
  font-size: 0.9em;
}
.num-cell {
  text-align: right !important;
}
.memo-cell {
  word-break: break-all;
}
.header-select {
  max-width: 120px;
}
</style>
